import { useCallback, useEffect, useRef } from 'react';

type IntersectHandler = (
  entry: IntersectionObserverEntry,
  observer: IntersectionObserver
) => void;

export const useIntersect = (
  onIntersect: IntersectHandler,
  options?: IntersectionObserverInit,
  listRef?: React.RefObject<HTMLUListElement>
) => {
  const ref = useRef<HTMLLIElement | HTMLDivElement>(null);
  const callback = useCallback(
    (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) onIntersect(entry, observer);
      });
    },
    [onIntersect]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(callback, options);
    if (!ref.current) {
      if (listRef?.current?.lastElementChild) {
        observer.observe(listRef.current.lastElementChild);
      } else {
        return;
      }
    } else {
      if (ref.current !== ref.current.parentElement?.lastElementChild) {
        observer.unobserve(ref.current);
        if (ref.current.parentElement?.lastElementChild) {
          observer.observe(ref.current.parentElement.lastElementChild);
        }
      } else {
        observer.observe(ref.current);
      }
    }
    return () => observer.disconnect();
  }, [ref, options, callback, listRef]);

  return ref;
};
